import {
  blue,
  cyan,
  geekblue,
  gray,
  green,
  magenta,
  orange,
  red,
  volcano,
  gold,
  lime,
  purple,
  yellow,
} from "@ant-design/colors";
import { AuthRoles } from "@lib/types/Auth";
import {
  HistoryStatus,
  InvoicePriority,
  InvoiceStatus,
  WalletAddressStatus,
  WalletAddressType,
  WalletType,
} from "@lib/types/Billing";
import {
  AuditLogActionTypeEnum,
  AuditLogStatusEnum,
} from "@lib/types/Monitoring";
import {
  XAICallStatus,
  XAIClientMood,
  XAIDisconnectionResult,
} from "@lib/types/xAI";
import { HoldingPeriodEnum, RiskLevelEnum } from "@lib/types/MarketPlace";
import {
  SendByteWalletAddressStatus,
  SendByteWalletAddressType,
  SendByteWalletType,
  SendByteWalletUsage,
} from "@lib/types/SendByte";
import {
  TransactionProxyStatus,
  TransactionProxyState,
} from "@lib/types/Transaction";
import dayjs from "dayjs";
import { getLuminance } from "polished";

const colors = [
  blue[7],
  magenta[7],
  cyan[7],
  orange[7],
  geekblue[7],
  gold[7],
  green[7],
  lime[7],
  purple[7],
  red[7],
  volcano[7],
  yellow[7],
];

export const getContrastColor = (bgColor: string) => {
  const color = getLuminance(bgColor);
  return color >= 0.5 ? "#000000" : "#FFFFFF";
};

export const randomColor = (colorIndex?: number) => {
  if (colorIndex === undefined || colorIndex === null) {
    return colors[Math.floor(Math.random() * colors.length)];
  }
  return colors[colorIndex % colors.length];
};

export const transactionStatusColor = (status: TransactionProxyStatus) => {
  switch (status) {
    case TransactionProxyStatus.Initialized:
      return blue[6];
    case TransactionProxyStatus.Pending:
      return orange[6];
    case TransactionProxyStatus.Confirmed:
      return green[6];
    case TransactionProxyStatus.AmountNotMatch:
      return red[6];
    case TransactionProxyStatus.ReferenceError:
      return red[6];
    case TransactionProxyStatus.Success:
      return green[6];
    case TransactionProxyStatus.Failed:
      return red[6];
    case TransactionProxyStatus.Canceled:
      return red[6];

    default:
      return red[4];
  }
};

export const transactionStateColor = (state: TransactionProxyState) => {
  switch (state) {
    case TransactionProxyState.PendingProviderApproval:
      return blue[6];
    case TransactionProxyState.ApprovedByProvider:
      return green[6];
    case TransactionProxyState.PendingForAccountInfo:
      return orange[6];
    case TransactionProxyState.ApprovedByCustomer:
      return green[6];
    case TransactionProxyState.CanceledByCustomer:
      return red[6];
    case TransactionProxyState.CanceledByProvider:
      return red[6];
    case TransactionProxyState.FailedByProvider:
      return red[6];
    case TransactionProxyState.FailedBySystem:
      return red[6];
    case TransactionProxyState.PendingCustomerApproval:
      return orange[6];

    default:
      return red[4];
  }
};

/**
 * Returns a color based on the invoice due date relative to today
 * @param date - The due date string
 * @param warningDays - Number of days before due date to show warning color (default: 3)
 * @returns Color string from ant-design colors
 */
export const invoiceDueDateColor = (
  date: string,
  warningDays: number = 3,
): string => {
  const today = dayjs().startOf("day");
  const dueDate = dayjs(date).startOf("day");
  const daysDiff = dueDate.diff(today, "day");

  // Past due date - Red
  if (daysDiff < 0) {
    return red[6];
  }

  // Today - Orange (urgent)
  if (daysDiff === 0) {
    return orange[6];
  }

  // Within warning days (1 to warningDays) - Yellow (approaching)
  if (daysDiff <= warningDays) {
    return yellow[4];
  }

  // More than warning days away - Green (safe)
  return green[6];
};

export const invoiceStatusColor = (status: InvoiceStatus) => {
  switch (status) {
    case InvoiceStatus.Paid:
      return green[6];
    case InvoiceStatus.PartiallyPaid:
      return cyan[6];
    case InvoiceStatus.Expired:
      return red[5];
    case InvoiceStatus.Pending:
      return orange[6];
    default:
      return gray[4];
  }
};

export const invoicePriorityColor = (priority: InvoicePriority) => {
  switch (priority) {
    case InvoicePriority.High:
      return "red";
    case InvoicePriority.Medium:
      return "blue";
    case InvoicePriority.Basic:
      return "green";
    default:
      return "black";
  }
};

export const walletAddressTypeImage = (
  type: WalletAddressType | SendByteWalletAddressType,
) => {
  switch (type) {
    case WalletAddressType.BTC || SendByteWalletAddressType.BTC:
      return "/assets/coins/btc.png";
    case WalletAddressType.ETH || SendByteWalletAddressType.ETH:
      return "/assets/coins/eth.png";
    case WalletAddressType.USDT || SendByteWalletAddressType.USDT:
      return "/assets/coins/usdt.png";
    default:
      return "";
  }
};

export const walletAddressStatus = (
  status: WalletAddressStatus | SendByteWalletAddressStatus,
) => {
  switch (status) {
    case WalletAddressStatus.Available || SendByteWalletAddressStatus.Available:
      return green[6];

    case WalletAddressStatus.InUse || SendByteWalletAddressStatus.InUse:
      return blue[6];

    case WalletAddressStatus.Reserved || SendByteWalletAddressStatus.Reserved:
      return orange[6];

    case WalletAddressStatus.Disabled || SendByteWalletAddressStatus.Disabled:
      return red[6];

    default:
      return "default";
  }
};

export const walletAddressTypeColor = (
  type: WalletAddressType | SendByteWalletAddressType,
) => {
  switch (type) {
    case WalletAddressType.BTC || SendByteWalletAddressType.BTC:
      return orange[6];

    case WalletAddressType.ETH || SendByteWalletAddressType.ETH:
      return geekblue[6];

    case WalletAddressType.USDT || SendByteWalletAddressType.USDT:
      return cyan[6];

    default:
      return "default";
  }
};

export const booleanColor = (status?: boolean) => {
  switch (status) {
    case true:
      return green[7];

    default:
      return red[7];
  }
};

export const walletAddressDisabledStatus = (status: boolean) => {
  switch (status) {
    case true:
      return red[6];

    case false:
      return green[6];

    default:
      return "default";
  }
};

export const walletTypeColor = (type: WalletType | SendByteWalletType) => {
  switch (type) {
    case WalletType.HOT || SendByteWalletType.HOT:
      return orange[6];

    case WalletType.COLD || SendByteWalletType.COLD:
      return blue[6];

    // case WalletType.HARDWARE:
    //   return "purple";

    // case WalletType.PAPER:
    //   return "gold";

    // case WalletType.MULTISIG:
    //   return "cyan";

    // case WalletType.CUSTODIAL:
    //   return "magenta";

    default:
      return "default";
  }
};

export const walletUsageTypeColor = (usage: SendByteWalletUsage) => {
  switch (usage) {
    case SendByteWalletUsage.WhiteLabel:
      return magenta[6];

    case SendByteWalletUsage.Lead:
      return green[6];

    default:
      return "default";
  }
};

export const userRoleColor = (role: AuthRoles) => {
  switch (role) {
    case AuthRoles.SuperAdmin: {
      return red[7];
    }
    case AuthRoles.Admin: {
      return cyan[7];
    }
    case AuthRoles.ProviderAgent: {
      return orange[7];
    }
    case AuthRoles.CustomerManager: {
      return magenta[7];
    }
    case AuthRoles.CustomerAgent: {
      return green[7];
    }
    case AuthRoles.UnknownTelegramUser: {
      return yellow[7];
    }
    default: {
      return gray[5];
    }
  }
};

export const historyStatusColor = (status: HistoryStatus) => {
  switch (status) {
    case HistoryStatus.Success:
      return green[7];
    case HistoryStatus.ScanError:
      return red[7];
    case HistoryStatus.FundsNotFound:
      return volcano[6];
    case HistoryStatus.InvoiceNotFound:
      return orange[6];
    case HistoryStatus.PendingForScan:
      return blue[6];
    case HistoryStatus.UnmatchedWallet:
      return red[9];

    default:
      return gray[5];
  }
};

export const auditLogActionTypeColor = (actionType: AuditLogActionTypeEnum) => {
  switch (actionType) {
    case AuditLogActionTypeEnum.Login:
      return cyan[7];
    case AuditLogActionTypeEnum.Create:
      return green[7];
    case AuditLogActionTypeEnum.Update:
      return geekblue[6];
    case AuditLogActionTypeEnum.Delete:
      return red[6];
    default:
      return gray[5];
  }
};

export const auditLogRequestStatusColor = (reqStatus: AuditLogStatusEnum) => {
  switch (reqStatus) {
    case AuditLogStatusEnum.Success:
      return green[7];
    case AuditLogStatusEnum.Failed:
      return red[6];
    default:
      return gray[5];
  }
};

export const xaiCallStatusColor = (status: XAICallStatus | string) => {
  const statusUpper =
    typeof status === "string" ? status.toUpperCase() : status;

  switch (statusUpper) {
    case XAICallStatus.Success:
      return green[6];
    case XAICallStatus.Ongoing:
      return purple[5];
    case XAICallStatus.Pending:
      return orange[5];
    case XAICallStatus.Added:
      return geekblue[5];
    case XAICallStatus.Error:
      return red[5];
    default:
      return gray[5];
  }
};

export const xaiClientMoodColor = (mood: XAIClientMood | string) => {
  const moodUpper = typeof mood === "string" ? mood.toUpperCase() : mood;

  switch (moodUpper) {
    case XAIClientMood.POSITIVE:
      return green[5];
    case XAIClientMood.NEGATIVE:
      return red[5];
    case XAIClientMood.NEUTRAL:
    case XAIClientMood.NÖTR:
      return gray[5];
    case XAIClientMood.UNKNOWN:
      return gray[5];

    default:
      return gray[5];
  }
};

export const xaiDisconnectionResultColor = (
  result: XAIDisconnectionResult | string,
) => {
  const resultUpper =
    typeof result === "string" ? result.toUpperCase() : result;

  switch (resultUpper) {
    case XAIDisconnectionResult.USER_HANGUP:
      return blue[6];
    case XAIDisconnectionResult.AGENT_HANGUP:
      return orange[6];
    case XAIDisconnectionResult.DIAL_BUSY:
    case XAIDisconnectionResult.DIAL_FAILED:
    case XAIDisconnectionResult.DIAL_NO_ANSWER:
      return yellow[6];
    case XAIDisconnectionResult.VOICEMAIL_REACHED:
      return cyan[6];
    case XAIDisconnectionResult.ERROR_NO_AUDIO_RECEIVED:
    case XAIDisconnectionResult.INVALID_DESTINATION:
      return red[6];
    case XAIDisconnectionResult.TELEPHONY_PROVIDER_PERMISSION_DENIED:
    case XAIDisconnectionResult.TELEPHONY_PROVIDER_UNAVAILABLE:
      return volcano[6];
    case XAIDisconnectionResult.INACTIVITY:
      return gray[6];
    default:
      return gray[5];
  }
};

export const tradeAdviseMarketBiasColor = (bias: string): string => {
  switch (bias) {
    case "bearish":
      return red[6];
    case "bullish":
      return green[6];
    default:
      return gray[5];
  }
};

export const tradeAdviseRiskLevelColor = (
  level: RiskLevelEnum | string,
): string => {
  switch (level) {
    case RiskLevelEnum.Low:
      return green[6];
    case RiskLevelEnum.Medium:
      return orange[6];
    case RiskLevelEnum.High:
      return red[6];
    default:
      return gray[5];
  }
};

export const tradeAdviseActionColor = (action: string): string => {
  if (action?.startsWith("SELL")) return volcano[6];
  if (action?.startsWith("BUY")) return green[6];
  return blue[6];
};

export const tradeAdviseTypeColor = (type: string): string => {
  switch (type) {
    case "pending":
      return orange[6];
    case "market":
      return blue[6];
    default:
      return gray[5];
  }
};

export const tradeAdviseHoldingPeriodColor = (
  period: HoldingPeriodEnum | string,
): string => {
  switch (period) {
    case HoldingPeriodEnum["1-2days"]:
      return green[6];
    case HoldingPeriodEnum["3-5days"]:
      return orange[6];
    case HoldingPeriodEnum["5-10days"]:
      return red[6];
    default:
      return gray[5];
  }
};
